import { t, useI18n } from '../../i18n';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Layer, Source } from '@vis.gl/react-maplibre';
import type { MapMouseEvent, MapRef, MarkerDragEvent } from '@vis.gl/react-maplibre';
import QRCode from 'qrcode';
import jsPDF from 'jspdf';
import ConfirmDeleteModal from '../../components/ConfirmDeleteModal';
import MapLibreMap from '../../components/maps/MapLibreMap';
import MapDotMarker from '../../components/maps/MapDotMarker';
import MapPinMarker from '../../components/maps/MapPinMarker';
import {
  clientService,
  type ClientContractRow,
} from '../../services/clientService';
import {
  patrolService,
  type CreatePatrolCheckpointInput,
} from '../../services/patrolService';
import {
  createCircleFeature,
  createFeatureCollection,
  createLineFeature,
  createPolygonFeature,
  getBoundsFromPositions,
  parseGeometryPoint,
  parseGeometryPolygon,
  type LatLngTuple,
} from '../../utils/geo';
import {
  buildPatrolRouteSlots,
  splitPatrolRoutePreview,
  type PatrolRouteCoverage,
  type PatrolRouteLeg,
} from '../../utils/patrolRoute';
import { decodePolyline } from '../../utils/polyline';
import {
  sanitizeMultilineText,
  sanitizeNumber,
  sanitizeText,
} from '../../utils/sanitize';
import logoSrc from '../../assets/Logo_Master.svg';
import './CreatePatrolPage.css';

function haversineDistance(
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number,
): number {
  const earthRadiusMetres = 6371000;
  const toRadians = (degrees: number) => (degrees * Math.PI) / 180;
  const deltaLat = toRadians(lat2 - lat1);
  const deltaLng = toRadians(lng2 - lng1);
  const a = Math.sin(deltaLat / 2) ** 2
    + Math.cos(toRadians(lat1))
    * Math.cos(toRadians(lat2))
    * Math.sin(deltaLng / 2) ** 2;
  return earthRadiusMetres * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

const DEFAULT_VIEW_STATE = {
  latitude: 53.3498,
  longitude: -6.2603,
  zoom: 13,
};

const ROUTE_STOP_PICK_RADIUS_PIXELS = 42;
const MERGE_SNAP_RADIUS_PIXELS = 30;

type BuilderStep = 'route' | 'configure';
type RouteStopSource = 'new' | 'existing';

interface Site {
  id: string;
  name: string;
  client_contract_id?: string | null;
  client_contract?: { name?: string | null } | null;
  attendance_radius_m?: number | null;
  geofence_type?: 'radius' | 'polygon' | string | null;
  geofence_radius_m?: number | null;
  geofence_polygon?: unknown;
  location?: unknown;
  [key: string]: unknown;
}

interface Checkpoint {
  id: string;
  name: string;
  code?: string | null;
  geofence_radius?: number | null;
  location?: unknown;
  lat: number | null;
  lng: number | null;
  [key: string]: unknown;
}

interface RouteStop {
  id: string;
  checkpointId?: string;
  source: RouteStopSource;
  sourceName: string;
  label: string;
  hasCustomLabel: boolean;
  qrRequired: boolean;
  lat: number;
  lng: number;
  geofenceRadius: number;
  code?: string | null;
}

interface Patrol {
  id: string;
  name: string;
  site_id: string;
  description?: string | null;
  estimated_duration_minutes?: number | null;
  is_loop?: boolean | null;
  sites?: { name?: string | null } | null;
  [key: string]: unknown;
}

interface PatrolPreviewCheckpoint {
  checkpointId: string;
  label: string;
  sourceName: string;
  code?: string | null;
  lat: number;
  lng: number;
  order: number;
  qrRequired: boolean;
}

interface QrCheckpoint {
  name: string;
  tokenId?: string | null;
  dataUrl?: string | null;
}

export interface SiteGeofenceEditTarget {
  siteId: string;
  clientContractId?: string | null;
}

function getDefaultCheckpointLabel(index: number): string {
  return `C${String(index + 1).padStart(2, '0')}`;
}

function appendLoopPosition(
  positions: Array<[number, number]>,
  isLoop: boolean,
): Array<[number, number]> {
  if (!isLoop || positions.length < 2) return positions;
  return [...positions, positions[0]];
}

function normaliseRouteStopLabels(stops: RouteStop[]): RouteStop[] {
  return stops.map((stop, index) => (
    stop.hasCustomLabel
      ? stop
      : { ...stop, label: getDefaultCheckpointLabel(index) }
  ));
}

function formatDistance(distanceMetres: number): string {
  if (distanceMetres < 1000) return `${Math.round(distanceMetres)} m`;
  return `${(distanceMetres / 1000).toFixed(2)} km`;
}

function formatGeofenceRadius(radiusMetres: number): string {
  if (radiusMetres < 1000) return `${Math.round(radiusMetres)} m radius`;
  return `${(radiusMetres / 1000).toFixed(1)} km radius`;
}

function formatCoordinates(lat: number, lng: number): string {
  return `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
}

function getSavedCheckpointName(stop: Pick<RouteStop, 'source' | 'sourceName' | 'label'>): string {
  if (stop.source === 'new' && stop.sourceName === 'New checkpoint') return stop.label;
  return stop.sourceName;
}

function getPolygonPositionsFromFeature(feature: ReturnType<typeof createPolygonFeature> | ReturnType<typeof createCircleFeature>): LatLngTuple[] {
  const coordinates = feature?.geometry.coordinates[0] ?? [];
  return coordinates.map(([lng, lat]) => [lat, lng] as LatLngTuple);
}

interface Props {
  clientContractId?: string | null;
  clientName?: string | null;
  onEditSiteGeofence?: (target: SiteGeofenceEditTarget) => void;
  onExit?: () => void;
  onReplayTour?: () => void;
}

export default function CreatePatrolPage({
  clientContractId = null,
  clientName: _clientName = null,
  onEditSiteGeofence,
  onExit,
  onReplayTour,
}: Props) {
  useI18n();
  const mapRef = useRef<MapRef | null>(null);
  const routeStopCounter = useRef(0);
  const skipNextAutoFitRef = useRef(false);
  const justDraggedRef = useRef(false);
  const markerClickRef = useRef(false);
  const clientContractPropRef = useRef(clientContractId ?? null);

  const [builderStep, setBuilderStep] = useState<BuilderStep>('route');
  const [clients, setClients] = useState<ClientContractRow[]>([]);
  const [selectedClientId, setSelectedClientId] = useState(clientContractId ?? '');
  const [sites, setSites] = useState<Site[]>([]);
  const [selectedSiteId, setSelectedSiteId] = useState('');
  const [availableCheckpoints, setAvailableCheckpoints] = useState<Checkpoint[]>([]);
  const [routeStops, setRouteStops] = useState<RouteStop[]>([]);
  const [selectedRouteStopId, setSelectedRouteStopId] = useState<string | null>(null);
  const [draggingRouteStopId, setDraggingRouteStopId] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [duration, setDuration] = useState('');
  const [description, setDescription] = useState('');
  const [completeLoop, setCompleteLoop] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [existingPatrols, setExistingPatrols] = useState<Patrol[]>([]);
  const [showPatrolId, setShowPatrolId] = useState<string | null>(null);
  const [showPatrolCheckpoints, setShowPatrolCheckpoints] = useState<PatrolPreviewCheckpoint[]>([]);
  const [showPatrolLoop, setShowPatrolLoop] = useState(false);
  const [showPatrolLegs, setShowPatrolLegs] = useState<PatrolRouteLeg[]>([]);
  const [routeCoverage, setRouteCoverage] = useState<Record<string, PatrolRouteCoverage>>({});
  const [routeTogglePatrolId, setRouteTogglePatrolId] = useState<string | null>(null);
  const [qrModalPatrol, setQrModalPatrol] = useState<Patrol | null>(null);
  const [qrData, setQrData] = useState<QrCheckpoint[]>([]);
  const [qrLoading, setQrLoading] = useState(false);
  const [editingPatrol, setEditingPatrol] = useState<Patrol | null>(null);
  const [deleteConfirmPatrolId, setDeleteConfirmPatrolId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const [drawMode, setDrawMode] = useState<'draw' | 'reuse'>('draw');
  const [reuseOpen, setReuseOpen] = useState(false);
  const [existingOpen, setExistingOpen] = useState(true);
  const [clearConfirmPending, setClearConfirmPending] = useState(false);
  const [exitConfirmPending, setExitConfirmPending] = useState(false);

  const clearPreviewPatrol = useCallback(() => {
    setShowPatrolId(null);
    setShowPatrolCheckpoints([]);
    setShowPatrolLoop(false);
    setShowPatrolLegs([]);
  }, []);

  const clearRouteBuilder = useCallback(() => {
    setBuilderStep('route');
    setRouteStops([]);
    setSelectedRouteStopId(null);
    setDraggingRouteStopId(null);
    setCompleteLoop(false);
  }, []);

  const loadClients = useCallback(async () => {
    try {
      const data = await clientService.fetchClientContracts();
      setClients(data);
    } catch (loadError) {
      console.error('Error loading clients:', (loadError as Error).message);
      setError(t('Unable to load clients for patrol setup'));
    }
  }, []);

  const loadSites = useCallback(async () => {
    try {
      const data = await patrolService.fetchSites();
      const rows = data as Site[];
      setSites(rows);
    } catch (loadError) {
      console.error('Error loading sites:', (loadError as Error).message);
      setError(t('Unable to load sites for patrol setup'));
    }
  }, []);

  // Recorded routes are an extra: when they cannot be read the list simply
  // shows no route status, and nothing else on the page is affected.
  const loadRouteCoverage = useCallback(async (patrolIds: string[]) => {
    try {
      setRouteCoverage(await patrolService.fetchPatrolRouteCoverage(patrolIds));
    } catch (routeError) {
      console.error('Error loading recorded routes:', (routeError as Error).message);
      setRouteCoverage({});
    }
  }, []);

  const loadPatrols = useCallback(async (siteId: string) => {
    if (!siteId) {
      setExistingPatrols([]);
      setRouteCoverage({});
      return;
    }

    try {
      const data = await patrolService.fetchPatrolsBySite(siteId);
      const patrols = data as Patrol[];
      setExistingPatrols(patrols);
      await loadRouteCoverage(patrols.map((patrol) => patrol.id));
    } catch (loadError) {
      console.error('Error loading patrols:', (loadError as Error).message);
      setExistingPatrols([]);
      setRouteCoverage({});
    }
  }, [loadRouteCoverage]);

  const loadCheckpoints = useCallback(async (siteId: string) => {
    try {
      const data = await patrolService.fetchCheckpointsBySite(siteId);
      const mapped = (data as Array<Checkpoint & { location?: unknown }>).map((checkpoint) => {
        const coords = parseGeometryPoint(checkpoint.location) as [number, number] | null;
        return {
          ...checkpoint,
          lat: coords?.[0] ?? null,
          lng: coords?.[1] ?? null,
        };
      });
      setAvailableCheckpoints(mapped);
    } catch (loadError) {
      console.error('Error loading checkpoints:', (loadError as Error).message);
    }
  }, []);

  const filteredSites = useMemo(
    () => (selectedClientId
      ? sites.filter((site) => site.client_contract_id === selectedClientId)
      : []),
    [selectedClientId, sites],
  );
  const selectedSite = useMemo(
    () => filteredSites.find((site) => site.id === selectedSiteId) ?? null,
    [filteredSites, selectedSiteId],
  );

  useEffect(() => {
    const initialise = async () => {
      setIsLoading(true);
      await Promise.all([loadClients(), loadSites()]);
      setIsLoading(false);
    };
    initialise();
  }, [loadClients, loadSites]);

  useEffect(() => {
    const nextClientContractId = clientContractId ?? null;
    if (clientContractPropRef.current === nextClientContractId) return;
    clientContractPropRef.current = nextClientContractId;
    if (!nextClientContractId) return;

    setSelectedClientId(nextClientContractId);
    setSelectedSiteId('');
    setAvailableCheckpoints([]);
    setExistingPatrols([]);
    clearRouteBuilder();
    clearPreviewPatrol();
  }, [clearPreviewPatrol, clearRouteBuilder, clientContractId]);

  useEffect(() => {
    if (!selectedSiteId) {
      setAvailableCheckpoints([]);
      setExistingPatrols([]);
      return;
    }
    loadCheckpoints(selectedSiteId);
    loadPatrols(selectedSiteId);
  }, [selectedSiteId, loadCheckpoints, loadPatrols]);

  useEffect(() => {
    if (!selectedSiteId) return;
    if (!filteredSites.some((site) => site.id === selectedSiteId)) {
      setSelectedSiteId('');
      setAvailableCheckpoints([]);
      setExistingPatrols([]);
      clearRouteBuilder();
    }
  }, [clearRouteBuilder, filteredSites, selectedSiteId]);

  const mutateRouteStops = useCallback((mutator: (current: RouteStop[]) => RouteStop[]) => {
    setRouteStops((current) => {
      const next = normaliseRouteStopLabels(mutator(current));
      if (next.length < 2) setCompleteLoop(false);
      return next;
    });
    clearPreviewPatrol();
    setSuccess('');
    setError('');
  }, [clearPreviewPatrol]);

  const addRouteStop = useCallback((stop: Omit<RouteStop, 'id' | 'label' | 'hasCustomLabel' | 'qrRequired'>) => {
    routeStopCounter.current += 1;
    const nextId = `route-stop-${routeStopCounter.current}`;
    mutateRouteStops((current) => [
      ...current,
      {
        ...stop,
        id: nextId,
        label: '',
        hasCustomLabel: false,
        qrRequired: true,
      },
    ]);
    setSelectedRouteStopId(nextId);
  }, [mutateRouteStops]);

  const updateRouteStopPosition = useCallback((stopId: string, lat: number, lng: number) => {
    setRouteStops((current) => current.map((stop) => (
      stop.id === stopId
        ? { ...stop, lat, lng }
        : stop
    )));
  }, []);

  const selectNearestRouteStop = useCallback((event: MapMouseEvent) => {
    if (!routeStops.length || !mapRef.current) return null;

    let nearestStopId: string | null = null;
    let nearestDistanceSq = Number.POSITIVE_INFINITY;

    routeStops.forEach((stop) => {
      const point = mapRef.current?.project([stop.lng, stop.lat]);
      if (!point) return;

      const deltaX = point.x - event.point.x;
      const deltaY = point.y - event.point.y;
      const distanceSq = deltaX ** 2 + deltaY ** 2;

      if (distanceSq < nearestDistanceSq) {
        nearestDistanceSq = distanceSq;
        nearestStopId = stop.id;
      }
    });

    if (nearestDistanceSq > ROUTE_STOP_PICK_RADIUS_PIXELS ** 2) {
      return null;
    }

    setSelectedRouteStopId(nearestStopId);
    return nearestStopId;
  }, [routeStops]);

  const handleMapClick = useCallback((event: MapMouseEvent) => {
    if (!selectedSiteId) return;
    if (markerClickRef.current) return;

    if (builderStep !== 'route') {
      // Configure step: clicking near a stop just selects it
      selectNearestRouteStop(event);
      return;
    }

    // Clicking near an existing stop selects it (opens inspector) rather than duplicating it
    if (routeStops.length && mapRef.current) {
      let nearestStop: RouteStop | null = null;
      let nearestDistanceSq = Number.POSITIVE_INFINITY;

      routeStops.forEach((stop) => {
        const point = mapRef.current?.project([stop.lng, stop.lat]);
        if (!point) return;
        const deltaX = point.x - event.point.x;
        const deltaY = point.y - event.point.y;
        const distanceSq = deltaX ** 2 + deltaY ** 2;
        if (distanceSq < nearestDistanceSq) {
          nearestDistanceSq = distanceSq;
          nearestStop = stop;
        }
      });

      if (nearestStop && nearestDistanceSq <= ROUTE_STOP_PICK_RADIUS_PIXELS ** 2) {
        setSelectedRouteStopId((nearestStop as RouteStop).id);
        return;
      }
    }

    addRouteStop({
      source: 'new',
      sourceName: 'New checkpoint',
      lat: event.lngLat.lat,
      lng: event.lngLat.lng,
      geofenceRadius: 50,
      code: null,
    });
  }, [addRouteStop, builderStep, routeStops, selectNearestRouteStop, selectedSiteId]);

  const handleReuseCheckpoint = useCallback((checkpoint: Checkpoint) => {
    if (checkpoint.lat == null || checkpoint.lng == null) return;
    addRouteStop({
      source: 'existing',
      checkpointId: checkpoint.id,
      sourceName: checkpoint.name,
      lat: checkpoint.lat,
      lng: checkpoint.lng,
      geofenceRadius: checkpoint.geofence_radius ?? 50,
      code: checkpoint.code ?? null,
    });
  }, [addRouteStop]);

  const handleRemoveRouteStop = useCallback((stopId: string) => {
    mutateRouteStops((current) => current.filter((stop) => stop.id !== stopId));
    setSelectedRouteStopId((current) => (current === stopId ? null : current));
    setDraggingRouteStopId((current) => (current === stopId ? null : current));
  }, [mutateRouteStops]);

  const moveRouteStop = useCallback((index: number, direction: number) => {
    mutateRouteStops((current) => {
      const targetIndex = index + direction;
      if (targetIndex < 0 || targetIndex >= current.length) return current;
      const nextStops = [...current];
      [nextStops[index], nextStops[targetIndex]] = [nextStops[targetIndex], nextStops[index]];
      return nextStops;
    });
  }, [mutateRouteStops]);

  const handleUndoLast = useCallback(() => {
    mutateRouteStops((current) => current.slice(0, -1));
  }, [mutateRouteStops]);

  const handleClearRoute = useCallback(() => {
    if (!routeStops.length) return;
    clearRouteBuilder();
    clearPreviewPatrol();
    setSuccess('');
    setError('');
  }, [clearPreviewPatrol, clearRouteBuilder, routeStops.length]);

  const handleRouteStopDragStart = useCallback((stopId: string) => {
    skipNextAutoFitRef.current = true;
    setDraggingRouteStopId(stopId);
    setSelectedRouteStopId(stopId);
    clearPreviewPatrol();
    setSuccess('');
    setError('');
  }, [clearPreviewPatrol]);

  const handleRouteStopDrag = useCallback((stopId: string, event: MarkerDragEvent) => {
    updateRouteStopPosition(stopId, event.lngLat.lat, event.lngLat.lng);
  }, [updateRouteStopPosition]);

  const handleRouteStopDragEnd = useCallback((stopId: string, event: MarkerDragEvent) => {
    const finalLat = event.lngLat.lat;
    const finalLng = event.lngLat.lng;
    const map = mapRef.current;

    setRouteStops((current) => {
      if (!map) {
        return current.map((stop) => (
          stop.id === stopId ? { ...stop, lat: finalLat, lng: finalLng } : stop
        ));
      }

      const draggedScreen = map.project([finalLng, finalLat]);

      // 1. Snap to existing site checkpoint (highest priority -- preserves existing patrols)
      let bestCheckpoint: Checkpoint | null = null;
      let bestCpDistSq = Number.POSITIVE_INFINITY;

      for (const cp of availableCheckpoints) {
        if (cp.lat == null || cp.lng == null) continue;
        const cpScreen = map.project([cp.lng!, cp.lat!]);
        const distSq = (cpScreen.x - draggedScreen.x) ** 2 + (cpScreen.y - draggedScreen.y) ** 2;
        if (distSq < bestCpDistSq) {
          bestCpDistSq = distSq;
          bestCheckpoint = cp;
        }
      }

      if (bestCheckpoint && bestCpDistSq <= MERGE_SNAP_RADIUS_PIXELS ** 2) {
        return normaliseRouteStopLabels(current.map((stop) => (
          stop.id === stopId
            ? {
                ...stop,
                lat: bestCheckpoint!.lat!,
                lng: bestCheckpoint!.lng!,
                source: 'existing' as RouteStopSource,
                checkpointId: bestCheckpoint!.id,
                sourceName: bestCheckpoint!.name,
                geofenceRadius: bestCheckpoint!.geofence_radius ?? 50,
                code: bestCheckpoint!.code ?? null,
              }
            : stop
        )));
      }

      // 2. Snap to another route stop already in the sequence
      let bestStop: RouteStop | null = null;
      let bestStopDistSq = Number.POSITIVE_INFINITY;

      for (const rs of current) {
        if (rs.id === stopId) continue;
        const rsScreen = map.project([rs.lng, rs.lat]);
        const distSq = (rsScreen.x - draggedScreen.x) ** 2 + (rsScreen.y - draggedScreen.y) ** 2;
        if (distSq < bestStopDistSq) {
          bestStopDistSq = distSq;
          bestStop = rs;
        }
      }

      if (bestStop && bestStopDistSq <= MERGE_SNAP_RADIUS_PIXELS ** 2) {
        const target = bestStop;
        return normaliseRouteStopLabels(current.map((stop) => {
          if (stop.id !== stopId) return stop;
          // Adopt the target's checkpoint reference if it has one
          if (target.source === 'existing') {
            return {
              ...stop,
              lat: target.lat,
              lng: target.lng,
              source: 'existing' as RouteStopSource,
              checkpointId: target.checkpointId,
              sourceName: target.sourceName,
              geofenceRadius: target.geofenceRadius,
              code: target.code,
            };
          }
          return { ...stop, lat: target.lat, lng: target.lng };
        }));
      }

      // 3. No snap target -- detach from existing checkpoint if it was reused
      return normaliseRouteStopLabels(current.map((stop) => {
        if (stop.id !== stopId) return stop;
        if (stop.source === 'existing') {
          return {
            ...stop,
            lat: finalLat,
            lng: finalLng,
            source: 'new' as RouteStopSource,
            checkpointId: undefined,
            sourceName: 'New checkpoint',
          };
        }
        return { ...stop, lat: finalLat, lng: finalLng };
      }));
    });

    setDraggingRouteStopId((current) => (current === stopId ? null : current));
    justDraggedRef.current = true;
    window.setTimeout(() => { justDraggedRef.current = false; }, 200);
  }, [availableCheckpoints]);

  const handleClientChange = useCallback((nextClientId: string) => {
    if (nextClientId === selectedClientId) return;

    if (routeStops.length > 0 && !window.confirm('Switching client will reset the unsaved route. Continue?')) {
      return;
    }

    setSelectedClientId(nextClientId);
    setSelectedSiteId('');
    setAvailableCheckpoints([]);
    setExistingPatrols([]);
    setEditingPatrol(null);
    clearRouteBuilder();
    clearPreviewPatrol();
    setSuccess('');
    setError('');
  }, [clearPreviewPatrol, clearRouteBuilder, routeStops.length, selectedClientId]);

  const handleSiteChange = useCallback((nextSiteId: string) => {
    if (nextSiteId === selectedSiteId) return;

    if (routeStops.length > 0 && !window.confirm('Switching site will reset the unsaved route. Continue?')) {
      return;
    }

    setSelectedSiteId(nextSiteId);
    clearRouteBuilder();
    clearPreviewPatrol();
    setEditingPatrol(null);
    setSuccess('');
    setError('');
  }, [clearPreviewPatrol, clearRouteBuilder, routeStops.length, selectedSiteId]);

  const handleLabelChange = useCallback((stopId: string, value: string) => {
    setRouteStops((current) => current.map((stop, index) => {
      if (stop.id !== stopId) return stop;
      const nextLabel = value.slice(0, 100);
      const defaultLabel = getDefaultCheckpointLabel(index);
      const trimmed = nextLabel.trim();
      return {
        ...stop,
        label: nextLabel,
        hasCustomLabel: trimmed !== '' && trimmed.toUpperCase() !== defaultLabel.toUpperCase(),
      };
    }));
    setError('');
    setSuccess('');
  }, []);

  const handleCheckpointNameChange = useCallback((stopId: string, value: string) => {
    setRouteStops((current) => current.map((stop) => (
      stop.id === stopId
        ? { ...stop, sourceName: value.slice(0, 100) }
        : stop
    )));
    setError('');
    setSuccess('');
  }, []);

  const handleCheckpointCodeChange = useCallback((stopId: string, value: string) => {
    setRouteStops((current) => current.map((stop) => (
      stop.id === stopId
        ? { ...stop, code: value.slice(0, 50) || null }
        : stop
    )));
    setError('');
    setSuccess('');
  }, []);

  const handleGeofenceRadiusChange = useCallback((stopId: string, value: string) => {
    const parsed = sanitizeNumber(value, { min: 1, max: 5000, integer: true });
    setRouteStops((current) => current.map((stop) => (
      stop.id === stopId
        ? { ...stop, geofenceRadius: parsed ?? stop.geofenceRadius }
        : stop
    )));
    setError('');
    setSuccess('');
  }, []);

  const handleToggleQrRequired = useCallback((stopId: string, qrRequired: boolean) => {
    setRouteStops((current) => current.map((stop) => (
      stop.id === stopId ? { ...stop, qrRequired } : stop
    )));
    setError('');
    setSuccess('');
  }, []);

  const handleSubmit = useCallback(async () => {
    const cleanName = sanitizeText(name, 100);
    const cleanDescription = sanitizeMultilineText(description, 1000);
    const cleanDuration = sanitizeNumber(duration, { min: 1, max: 1440, integer: true });

    if (!selectedSiteId) {
      setError(t('Select a site before saving the patrol'));
      return;
    }
    if (!cleanName) {
      setError(t('Patrol name is required'));
      return;
    }
    if (routeStops.length === 0) {
      setError(t('Add at least one checkpoint to the route before saving'));
      return;
    }

    const preparedStops = routeStops.map((stop, index) => ({
      ...stop,
      label: sanitizeText(stop.label, 100) || getDefaultCheckpointLabel(index),
    }));

    const labels = new Set<string>();
    for (const stop of preparedStops) {
      const key = stop.label.trim().toLowerCase();
      if (labels.has(key)) {
        setError(t('Checkpoint labels must be unique within the patrol'));
        return;
      }
      labels.add(key);
    }

    const checkpointPayload: CreatePatrolCheckpointInput[] = preparedStops.map((stop) => ({
      checkpointId: stop.checkpointId,
      label: stop.label,
      checkpointName: sanitizeText(getSavedCheckpointName(stop), 100) || stop.label,
      latitude: stop.lat,
      longitude: stop.lng,
      qrRequired: stop.qrRequired,
      code: sanitizeText(stop.code ?? '', 50) || null,
      geofenceRadius: stop.geofenceRadius,
    }));

    setIsSubmitting(true);
    setError('');
    setSuccess('');

    const params = {
      name: cleanName,
      siteId: selectedSiteId,
      durationMinutes: cleanDuration,
      description: cleanDescription || undefined,
      isLoop: completeLoop,
      checkpoints: checkpointPayload,
    };

    try {
      if (editingPatrol) {
        await patrolService.updatePatrol(editingPatrol.id, params);
        setSuccess(t('Patrol updated successfully'));
        setEditingPatrol(null);
      } else {
        await patrolService.createPatrol(params);
        setSuccess(t('Patrol created successfully'));
      }
      setName('');
      setDuration('');
      setDescription('');
      clearRouteBuilder();
      clearPreviewPatrol();
      await loadCheckpoints(selectedSiteId);
      await loadPatrols(selectedSiteId);
    } catch (submitError) {
      setError((submitError as Error).message || (editingPatrol
        ? t('Failed to update patrol')
        : t('Failed to create patrol')));
    } finally {
      setIsSubmitting(false);
    }
  }, [
    clearPreviewPatrol,
    clearRouteBuilder,
    completeLoop,
    description,
    duration,
    editingPatrol,
    loadCheckpoints,
    loadPatrols,
    name,
    routeStops,
    selectedSiteId,
  ]);

  const handleCancelEdit = useCallback(() => {
    setEditingPatrol(null);
    setName('');
    setDuration('');
    setDescription('');
    clearRouteBuilder();
    clearPreviewPatrol();
    setError('');
    setSuccess('');
  }, [clearPreviewPatrol, clearRouteBuilder]);

  const handleEditPatrol = useCallback(async (patrol: Patrol) => {
    try {
      const data = await patrolService.fetchPatrolCheckpoints(patrol.id);
      const stops: RouteStop[] = (data as Array<{
        checkpoint_id: string;
        sequence_order: number;
        is_required?: boolean | null;
        label?: string | null;
        checkpoints?: {
          name?: string | null;
          code?: string | null;
          geofence_radius?: number | null;
          location?: unknown;
        } | null;
      }>)
        .sort((a, b) => a.sequence_order - b.sequence_order)
        .map((row, index): RouteStop | null => {
          const coords = parseGeometryPoint(row.checkpoints?.location) as [number, number] | null;
          if (!coords) return null;
          routeStopCounter.current += 1;
          const defaultLabel = getDefaultCheckpointLabel(index);
          const label = row.label || row.checkpoints?.name || defaultLabel;
          const trimmed = label.trim();
          return {
            id: `route-stop-${routeStopCounter.current}`,
            checkpointId: row.checkpoint_id,
            source: 'existing',
            sourceName: row.checkpoints?.name || 'Checkpoint',
            label,
            hasCustomLabel: trimmed !== '' && trimmed.toUpperCase() !== defaultLabel.toUpperCase(),
            qrRequired: row.is_required !== false,
            lat: coords[0],
            lng: coords[1],
            geofenceRadius: row.checkpoints?.geofence_radius ?? 50,
            code: row.checkpoints?.code ?? null,
          };
        })
        .filter((stop): stop is RouteStop => stop !== null);

      setEditingPatrol(patrol);
      setName(patrol.name);
      setDuration(patrol.estimated_duration_minutes ? String(patrol.estimated_duration_minutes) : '');
      setDescription(patrol.description || '');
      setCompleteLoop(Boolean(patrol.is_loop));
      const patrolSite = sites.find((site) => site.id === patrol.site_id);
      if (patrolSite?.client_contract_id) {
        setSelectedClientId(patrolSite.client_contract_id);
      }
      setSelectedSiteId(patrol.site_id);
      setRouteStops(stops);
      setSelectedRouteStopId(null);
      setDraggingRouteStopId(null);
      setBuilderStep('route');
      clearPreviewPatrol();
      setError('');
      setSuccess('');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (editError) {
      alert(`Failed to load patrol for editing: ${(editError as Error).message}`);
    }
  }, [clearPreviewPatrol, sites]);

  const handleConfirmDelete = useCallback(async () => {
    if (!deleteConfirmPatrolId) return;
    setIsDeleting(true);
    try {
      await patrolService.deletePatrol(deleteConfirmPatrolId);
      if (showPatrolId === deleteConfirmPatrolId) clearPreviewPatrol();
      setDeleteConfirmPatrolId(null);
      await loadPatrols(selectedSiteId);
    } catch (deleteError) {
      alert(`Failed to delete patrol: ${(deleteError as Error).message}`);
    } finally {
      setIsDeleting(false);
    }
  }, [clearPreviewPatrol, deleteConfirmPatrolId, loadPatrols, selectedSiteId, showPatrolId]);

  const handleShowPatrol = useCallback(async (patrol: Patrol) => {
    if (showPatrolId === patrol.id) {
      clearPreviewPatrol();
      return;
    }

    try {
      const data = await patrolService.fetchPatrolCheckpoints(patrol.id);
      const parsed = (data as Array<{
        checkpoint_id: string;
        sequence_order: number;
        is_required?: boolean | null;
        label?: string | null;
        checkpoints?: {
          location?: unknown;
          name?: string | null;
          code?: string | null;
        } | null;
      }>)
        .map((checkpoint): PatrolPreviewCheckpoint | null => {
          const coords = parseGeometryPoint(checkpoint.checkpoints?.location) as [number, number] | null;
          if (!coords) return null;
          return {
            checkpointId: checkpoint.checkpoint_id,
            label: checkpoint.label || checkpoint.checkpoints?.name || `Checkpoint ${checkpoint.sequence_order}`,
            sourceName: checkpoint.checkpoints?.name || 'Checkpoint',
            code: checkpoint.checkpoints?.code || null,
            lat: coords[0],
            lng: coords[1],
            order: checkpoint.sequence_order,
            qrRequired: checkpoint.is_required !== false,
          };
        })
        .filter((checkpoint): checkpoint is PatrolPreviewCheckpoint => checkpoint !== null)
        .sort((left, right) => left.order - right.order);

      // Read the legs afresh so they match the checkpoints just loaded.
      let legs: PatrolRouteLeg[] = [];
      try {
        const rows = await patrolService.fetchPatrolRouteLegs([patrol.id]);
        legs = rows.map((row) => ({
          fromCheckpointId: row.from_checkpoint_id,
          toCheckpointId: row.to_checkpoint_id,
          positions: decodePolyline(row.polyline),
        }));
      } catch (routeError) {
        console.error('Error loading recorded route:', (routeError as Error).message);
      }

      setShowPatrolId(patrol.id);
      setShowPatrolCheckpoints(parsed);
      setShowPatrolLoop(Boolean(patrol.is_loop));
      setShowPatrolLegs(legs);
    } catch (previewError) {
      console.error('Error loading patrol checkpoints:', (previewError as Error).message);
    }
  }, [clearPreviewPatrol, showPatrolId]);

  const handleToggleRouteRecording = useCallback(async (patrol: Patrol) => {
    const coverage = routeCoverage[patrol.id];
    if (!coverage) return;
    const enable = !coverage.recordingEnabled;
    if (!enable && !window.confirm(t('Turn off the recorded route for this patrol? Its recorded path is deleted and guards see straight lines between checkpoints.'))) {
      return;
    }

    setRouteTogglePatrolId(patrol.id);
    try {
      await patrolService.setPatrolRouteRecording(patrol.id, enable);
      if (!enable && showPatrolId === patrol.id) setShowPatrolLegs([]);
      await loadRouteCoverage(existingPatrols.map((existing) => existing.id));
    } catch (toggleError) {
      alert(`${t('Failed to update the recorded route')}: ${(toggleError as Error).message}`);
    } finally {
      setRouteTogglePatrolId(null);
    }
  }, [existingPatrols, loadRouteCoverage, routeCoverage, showPatrolId]);

  const renderPatrolRouteStatus = (patrol: Patrol) => {
    const coverage = routeCoverage[patrol.id];
    // Nothing to say without route data, or for a patrol with fewer than two
    // QR checkpoints, which has no legs to record.
    if (!coverage || coverage.total === 0) return null;

    let label: string;
    if (!coverage.recordingEnabled) label = t('Recorded route off');
    else if (coverage.recorded === 0) label = t('No recorded route yet');
    else if (coverage.recorded >= coverage.total) label = t('Recorded route');
    else label = t('Recording route: {done} of {total} legs', { done: coverage.recorded, total: coverage.total });

    return (
      <span
        className="pb-existing__route"
        title={t('Recorded from the path guards walk between checkpoint scans. Updated overnight.')}
      >
        {label}
        <button
          type="button"
          className="pb-existing__route-toggle"
          disabled={routeTogglePatrolId === patrol.id}
          onClick={() => handleToggleRouteRecording(patrol)}
        >
          {coverage.recordingEnabled ? t('Turn off') : t('Turn on')}
        </button>
      </span>
    );
  };

  const handleOpenQrModal = useCallback(async (patrol: Patrol) => {
    setQrModalPatrol(patrol);
    setQrLoading(true);
    try {
      const checkpoints = await patrolService.fetchCheckpointQrCodes(patrol.id);
      const withImages = await Promise.all(
        (checkpoints as QrCheckpoint[]).map(async (checkpoint) => {
          let dataUrl: string | null = null;
          if (checkpoint.tokenId) {
            dataUrl = await QRCode.toDataURL(checkpoint.tokenId, { width: 220, margin: 1 });
          }
          return { ...checkpoint, dataUrl };
        }),
      );
      setQrData(withImages);
    } catch (qrError) {
      console.error('Error loading QR codes:', (qrError as Error).message);
      setQrData([]);
    } finally {
      setQrLoading(false);
    }
  }, []);

  const handlePrintQr = useCallback(() => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const html = `
      <html>
        <head>
          <title>QR Codes - ${qrModalPatrol?.name || 'Patrol'}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 24px; }
            h1 { font-size: 18px; margin-bottom: 20px; }
            .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; }
            .item { text-align: center; page-break-inside: avoid; }
            .item img { width: 160px; height: 160px; }
            .item p { margin: 10px 0 0; font-size: 14px; font-weight: 600; }
          </style>
        </head>
        <body>
          <h1>QR Codes - ${qrModalPatrol?.name || 'Patrol'}</h1>
          <div class="grid">
            ${qrData.map((checkpoint) => `
              <div class="item">
                ${checkpoint.dataUrl ? `<img src="${checkpoint.dataUrl}" alt="${checkpoint.name}" />` : '<p>[No QR code]</p>'}
                <p>${checkpoint.name}</p>
              </div>
            `).join('')}
          </div>
        </body>
      </html>
    `;

    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.onload = () => printWindow.print();
  }, [qrData, qrModalPatrol?.name]);

  const handleDownloadQrPdf = useCallback(() => {
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const columns = 2;
    const qrSize = 50;
    const colWidth = (pageWidth - 40) / columns;
    const startX = 20;
    const startY = 30;
    const rowHeight = qrSize + 20;
    const rowsPerPage = Math.floor((pageHeight - startY - 10) / rowHeight);

    pdf.setFontSize(16);
    pdf.text(`QR Codes - ${qrModalPatrol?.name || 'Patrol'}`, startX, 18);

    qrData.forEach((checkpoint, index) => {
      const pageIndex = Math.floor(Math.floor(index / columns) / rowsPerPage);
      while (pdf.internal.pages.length - 1 < pageIndex + 1) {
        pdf.addPage();
      }

      pdf.setPage(pageIndex + 1);
      if (pageIndex > 0) {
        pdf.setFontSize(16);
        pdf.text(`QR Codes - ${qrModalPatrol?.name || 'Patrol'}`, startX, 18);
      }

      const column = index % columns;
      const row = Math.floor(index / columns) % rowsPerPage;
      const x = startX + column * (colWidth + 10);
      const y = startY + row * rowHeight;

      if (checkpoint.dataUrl) {
        pdf.addImage(checkpoint.dataUrl, 'PNG', x + (colWidth - qrSize) / 2, y, qrSize, qrSize);
      }

      pdf.setFontSize(11);
      pdf.text(checkpoint.name, x + colWidth / 2, y + qrSize + 6, { align: 'center' });
    });

    pdf.save(`${(qrModalPatrol?.name || 'patrol').replace(/\s+/g, '-').toLowerCase()}-qr-codes.pdf`);
  }, [qrData, qrModalPatrol?.name]);

  const siteFallbackCenter = useMemo(
    () => parseGeometryPoint(selectedSite?.location) as [number, number] | null,
    [selectedSite],
  );
  const sitePolygonPositions = useMemo(
    () => parseGeometryPolygon(selectedSite?.geofence_polygon) ?? [],
    [selectedSite],
  );
  const siteGeofenceRadius = useMemo(
    () => selectedSite?.geofence_radius_m ?? selectedSite?.attendance_radius_m ?? 300,
    [selectedSite],
  );
  const siteGeofenceFeature = useMemo(() => {
    if (!selectedSite) return null;

    if (selectedSite.geofence_type === 'polygon' && sitePolygonPositions.length >= 3) {
      return createPolygonFeature(sitePolygonPositions, { kind: 'site-geofence' });
    }

    return siteFallbackCenter
      ? createCircleFeature(siteFallbackCenter, siteGeofenceRadius, 96, { kind: 'site-geofence' })
      : null;
  }, [selectedSite, siteFallbackCenter, siteGeofenceRadius, sitePolygonPositions]);
  const siteGeofencePositions = useMemo(
    () => getPolygonPositionsFromFeature(siteGeofenceFeature),
    [siteGeofenceFeature],
  );
  const siteGeofenceLabel = useMemo(() => {
    if (!selectedSite) return t('Select a site');
    if (selectedSite.geofence_type === 'polygon' && sitePolygonPositions.length >= 3) {
      return `Site geofence bounds (${sitePolygonPositions.length} points)`;
    }
    return formatGeofenceRadius(siteGeofenceRadius);
  }, [selectedSite, siteGeofenceRadius, sitePolygonPositions.length]);

  const routePositions = useMemo(
    () => routeStops.map((stop): [number, number] => [stop.lat, stop.lng]),
    [routeStops],
  );
  const routeDisplayPositions = useMemo(
    () => appendLoopPosition(routePositions, completeLoop),
    [completeLoop, routePositions],
  );
  const routeFeature = useMemo(
    () => createLineFeature(routeDisplayPositions),
    [routeDisplayPositions],
  );
  const routeDistanceMetres = useMemo(
    () => routeDisplayPositions.reduce((total, position, index) => {
      if (index === 0) return 0;
      const [prevLat, prevLng] = routeDisplayPositions[index - 1];
      const [lat, lng] = position;
      return total + haversineDistance(prevLat, prevLng, lat, lng);
    }, 0),
    [routeDisplayPositions],
  );

  const previewRoutePositions = useMemo(
    () => showPatrolCheckpoints.map((checkpoint): [number, number] => [checkpoint.lat, checkpoint.lng]),
    [showPatrolCheckpoints],
  );
  const previewRouteDisplayPositions = useMemo(
    () => appendLoopPosition(previewRoutePositions, showPatrolLoop),
    [previewRoutePositions, showPatrolLoop],
  );
  const previewRouteLines = useMemo<{ recorded: LatLngTuple[][]; straight: LatLngTuple[][] }>(() => {
    const slots = buildPatrolRouteSlots(showPatrolCheckpoints, showPatrolLegs);
    // Fewer than two QR checkpoints means no legs to record: draw it as before.
    if (slots.length === 0) return { recorded: [], straight: [previewRouteDisplayPositions] };
    return splitPatrolRoutePreview(slots, showPatrolLoop);
  }, [previewRouteDisplayPositions, showPatrolCheckpoints, showPatrolLegs, showPatrolLoop]);
  const previewRouteFeature = useMemo(() => {
    const collection = createFeatureCollection(
      previewRouteLines.straight.map((positions) => createLineFeature(positions)),
    );
    return collection.features.length > 0 ? collection : null;
  }, [previewRouteLines]);
  const previewRecordedFeature = useMemo(() => {
    const collection = createFeatureCollection(
      previewRouteLines.recorded.map((positions) => createLineFeature(positions)),
    );
    return collection.features.length > 0 ? collection : null;
  }, [previewRouteLines]);

  const siteCheckpointPositions = useMemo(
    () => availableCheckpoints
      .filter((checkpoint) => checkpoint.lat != null && checkpoint.lng != null)
      .map((checkpoint): [number, number] => [checkpoint.lat!, checkpoint.lng!]),
    [availableCheckpoints],
  );

  const fitPoints = useMemo(() => {
    if (showPatrolCheckpoints.length > 0) return previewRoutePositions;
    if (routePositions.length > 0) return routePositions;
    return [...siteGeofencePositions, ...siteCheckpointPositions];
  }, [
    previewRoutePositions,
    routePositions,
    showPatrolCheckpoints.length,
    siteCheckpointPositions,
    siteGeofencePositions,
  ]);

  const fitPointsKey = useMemo(
    () => fitPoints.map((point) => `${point[0]},${point[1]}`).join('|'),
    [fitPoints],
  );

  useEffect(() => {
    if (draggingRouteStopId) return;
    if (skipNextAutoFitRef.current) {
      skipNextAutoFitRef.current = false;
      return;
    }
    if (!mapRef.current) return;

    if (fitPoints.length > 1) {
      const bounds = getBoundsFromPositions(fitPoints);
      if (bounds) {
        mapRef.current.fitBounds(bounds, { padding: 70, maxZoom: 17, duration: 900 });
      }
      return;
    }

    if (fitPoints.length === 1) {
      mapRef.current.flyTo({
        center: [fitPoints[0][1], fitPoints[0][0]],
        zoom: 16.3,
        duration: 900,
      });
      return;
    }

    if (siteFallbackCenter) {
      mapRef.current.flyTo({
        center: [siteFallbackCenter[1], siteFallbackCenter[0]],
        zoom: 15.2,
        duration: 900,
      });
    }
  }, [draggingRouteStopId, fitPoints, fitPointsKey, siteFallbackCenter]);

  const selectedRouteStopIndex = useMemo(
    () => routeStops.findIndex((stop) => stop.id === selectedRouteStopId),
    [routeStops, selectedRouteStopId],
  );
  const selectedRouteStop = selectedRouteStopIndex >= 0 ? routeStops[selectedRouteStopIndex] : null;

  const estDurationMinutes = useMemo(() => {
    if (routeDistanceMetres === 0) return null;
    const walkSec = routeDistanceMetres / 1.4;
    const qrSec = routeStops.filter((s) => s.qrRequired).length * 30;
    return Math.round((walkSec + qrSec) / 60);
  }, [routeDistanceMetres, routeStops]);

  if (isLoading) {
    return <div className="loading"><div className="spinner" /></div>;
  }

  const usedCheckpointIds = new Set(
    routeStops.filter((s) => s.checkpointId).map((s) => s.checkpointId!),
  );

  return (
    <div className="pb-wrapper">
      <div className="pb-app">
        {/* â”€â”€ Topbar â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
        <header className="pb-top">
          <button
            type="button"
            className="pb-top__brand pb-top__brand--btn"
            onClick={() => {
              if (!onExit) return;
              if (routeStops.length > 0 || name.trim()) {
                setExitConfirmPending(true);
              } else {
                onExit();
              }
            }}
            title={t('Back to dashboard')}
          >
            <img src={logoSrc} alt={t('OdinsEye')} className="pb-top__logo-img" />
            <div className="pb-top__title-stack">
              <span className="pb-top__title">OdinsEye</span>
              <span className="pb-top__sub">{t('Patrol builder')}</span>
            </div>
          </button>

          <div className="pb-top__crumbs">
            <span className="pb-top__crumb">{t('Sites & Patrols')}</span>
            <span className="pb-top__crumb-sep">/</span>
            <span className="pb-top__crumb">{selectedSite?.name ?? t('Select a site')}</span>
            <span className="pb-top__crumb-sep">/</span>
            <span className="pb-top__crumb pb-top__crumb--active">
              {editingPatrol ? `Editing: ${editingPatrol.name}` : 'New patrol'}
            </span>
            {(success || isSubmitting) && (
              <span className="pb-top__autosave">
                <span className="pb-top__autosave-dot" />
                {isSubmitting ? 'Saving...' : success}
              </span>
            )}
          </div>

          <div className="pb-top__right" data-tour="patrol-builder-actions">
            {onExit && (
              <button
                type="button"
                className="pb-btn pb-btn--ghost pb-btn--exit"
                onClick={() => {
                  if (routeStops.length > 0 || name.trim()) {
                    setExitConfirmPending(true);
                  } else {
                    onExit();
                  }
                }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M12 5l-7 7 7 7" /></svg>
                {t('Back to Sites')}
              </button>
            )}
            {onReplayTour && (
              <button type="button" className="pb-btn pb-btn--ghost" onClick={onReplayTour}>
                {t('Help')}
              </button>
            )}
            {editingPatrol ? (
              <button type="button" className="pb-btn pb-btn--ghost" onClick={handleCancelEdit}>{t('Cancel edit')}</button>
            ) : null}
            <button
              type="button"
              className="pb-btn pb-btn--primary"
              onClick={handleSubmit}
              disabled={isSubmitting || !selectedSiteId || !name.trim() || routeStops.length === 0}
            >
              {isSubmitting ? t('Saving...') : editingPatrol ? t('Save changes') : t('Save patrol')}
            </button>
          </div>
        </header>

        {/* â”€â”€ Three-pane body â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
        <div className={`pb-body${!selectedRouteStop ? ' pb-body--no-inspector' : ''}`}>

          {/* â”€â”€ LEFT RAIL â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
          <aside className="pb-rail" data-tour="patrol-builder-library">
            <div className="pb-rail__scroll">

              {/* Site + client pickers */}
              <div className="pb-card" data-tour="patrol-builder-site">
                <div className="pb-site__row">
                  <select
                    className="form__select form__select--sm pb-select"
                    value={selectedClientId}
                    onChange={(e) => handleClientChange(e.target.value)}
                    aria-label={t('Client')}
                  >
                    <option value="">{t('Client...')}</option>
                    {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                  <select
                    className="form__select form__select--sm pb-select"
                    value={selectedSiteId}
                    onChange={(e) => handleSiteChange(e.target.value)}
                    disabled={!selectedClientId}
                    aria-label={t('Site')}
                  >
                    <option value="">
                      {!selectedClientId ? 'Site...' : filteredSites.length === 0 ? 'No sites' : 'Site...'}
                    </option>
                    {filteredSites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </div>
                {selectedSite && (
                  <div className="pb-site__meta">
                    <span className="pb-site__meta-item">
                      <strong>{siteGeofenceLabel}</strong>
                    </span>
                    {onEditSiteGeofence && (
                      <>
                        <span className="pb-site__meta-sep" />
                        <span
                          className="pb-site__meta-item"
                          style={{ cursor: 'pointer', color: 'var(--primary-light)' }}
                          onClick={() => onEditSiteGeofence({ siteId: selectedSite.id, clientContractId: selectedSite.client_contract_id ?? selectedClientId })}
                        >
                          {t('Edit bounds')}
                        </span>
                      </>
                    )}
                  </div>
                )}
              </div>

              {/* Patrol details */}
              <div className="pb-card" data-tour="patrol-builder-details">
                <input
                  className="pb-input"
                  placeholder={t('Patrol name...')}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={!selectedSiteId}
                  style={{ fontSize: 13, fontWeight: 600 }}
                />
                <div className="pb-site__row">
                  <input
                    className="pb-input pb-input--mono"
                    placeholder={estDurationMinutes ? `${estDurationMinutes} min (auto)` : 'Duration (min)'}
                    value={duration}
                    onChange={(e) => setDuration(e.target.value)}
                    disabled={!selectedSiteId}
                    type="number"
                  />
                </div>
                <textarea
                  className="pb-textarea"
                  rows={2}
                  placeholder={t('Notes for the guards (optional)')}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  disabled={!selectedSiteId}
                />
              </div>

              {/* Mode + route stops */}
              <div className="pb-card" data-tour="patrol-builder-route">
                <div className="pb-modesw">
                  <button
                    type="button"
                    className={`pb-modesw__btn${drawMode === 'draw' ? ' pb-modesw__btn--on' : ''}`}
                    onClick={() => setDrawMode('draw')}
                  >
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><line x1="12" y1="3" x2="12" y2="6" /><line x1="12" y1="18" x2="12" y2="21" /><line x1="3" y1="12" x2="6" y2="12" /><line x1="18" y1="12" x2="21" y2="12" /></svg>
                    {t('Draw')}
                  </button>
                  <button
                    type="button"
                    className={`pb-modesw__btn${drawMode === 'reuse' ? ' pb-modesw__btn--on' : ''}`}
                    onClick={() => setDrawMode('reuse')}
                  >
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2a7 7 0 017 7c0 5-7 13-7 13S5 14 5 9a7 7 0 017-7z" /><circle cx="12" cy="9" r="2.5" /></svg>
                    {t('Reuse')}
                  </button>
                </div>

                <div className="pb-mini-header">
                  <span>{t('Route stops')}</span>
                  <small>{routeStops.length} &middot; {formatDistance(routeDistanceMetres)}</small>
                </div>

                {routeStops.length === 0 ? (
                  <div className="pb-stops__empty">
                    <strong>{t('No stops yet')}</strong>
                    {selectedSiteId
                      ? 'Click anywhere inside the geofence on the map to drop your first checkpoint.'
                      : 'Select a site to unlock the map.'}
                  </div>
                ) : (
                  <div className="pb-stops">
                    {routeStops.map((stop, index) => (
                      <div
                        key={stop.id}
                        className={`pb-stop${selectedRouteStopId === stop.id ? ' pb-stop--active' : ''}${stop.source === 'existing' ? ' pb-stop--reuse' : ''}`}
                        onClick={() => setSelectedRouteStopId(stop.id)}
                      >
                        <div className="pb-stop__pill">{index + 1}</div>
                        <div className="pb-stop__body">
                          <div className="pb-stop__name">{stop.label}</div>
                          <div className="pb-stop__meta">
                            <span>{stop.source === 'existing' ? stop.sourceName : 'New checkpoint'}</span>
                            <span className="dot" />
                            <span>{stop.geofenceRadius}m</span>
                            {stop.qrRequired && <><span className="dot" /><span>QR</span></>}
                          </div>
                        </div>
                        <div className="pb-stop__actions">
                          <button
                            type="button"
                            className="pb-stop__abtn"
                            disabled={index === 0}
                            onClick={(e) => { e.stopPropagation(); moveRouteStop(index, -1); }}
                            title={t('Move earlier')}
                            style={{ transform: 'rotate(180deg)' }}
                          >
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6" /></svg>
                          </button>
                          <button
                            type="button"
                            className="pb-stop__abtn"
                            disabled={index === routeStops.length - 1}
                            onClick={(e) => { e.stopPropagation(); moveRouteStop(index, 1); }}
                            title={t('Move later')}
                          >
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6" /></svg>
                          </button>
                          <button
                            type="button"
                            className="pb-stop__abtn pb-stop__abtn--danger"
                            onClick={(e) => { e.stopPropagation(); handleRemoveRouteStop(stop.id); }}
                            title={t('Remove')}
                          >
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6L6 18M6 6l12 12" /></svg>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {routeStops.length >= 2 && (
                  <div className={`pb-loop-row${completeLoop ? ' pb-loop-row--active' : ''}`}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><path d="M17 1l4 4-4 4" /><path d="M3 11V9a4 4 0 014-4h14" /><path d="M7 23l-4-4 4-4" /><path d="M21 13v2a4 4 0 01-4 4H3" /></svg>
                    <span>{completeLoop ? 'Loop enabled' : 'Close loop'}</span>
                    <button
                      type="button"
                      className={`pb-switch${completeLoop ? ' pb-switch--on' : ''}`}
                      onClick={() => setCompleteLoop((c) => !c)}
                      title={t('Toggle loop')}
                    />
                  </div>
                )}
              </div>

              {/* Reusable checkpoints -- collapsible */}
              {selectedSiteId && availableCheckpoints.length > 0 && (
                <div className={`pb-disclosure${reuseOpen ? ' pb-disclosure--open' : ''}`}>
                  <button type="button" className="pb-disclosure__head" onClick={() => setReuseOpen((o) => !o)}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2a7 7 0 017 7c0 5-7 13-7 13S5 14 5 9a7 7 0 017-7z" /><circle cx="12" cy="9" r="2.5" /></svg>
                    Reusable checkpoints
                    <small>{availableCheckpoints.length}</small>
                    <span className="pb-disclosure__chev">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6" /></svg>
                    </span>
                  </button>
                  {reuseOpen && (
                    <div className="pb-disclosure__body">
                      <div className="pb-reuse-grid">
                        {availableCheckpoints.map((cp) => {
                          const used = usedCheckpointIds.has(cp.id);
                          return (
                            <button
                              key={cp.id}
                              type="button"
                              className="pb-reuse-chip"
                              disabled={used || cp.lat == null}
                              onClick={() => !used && cp.lat != null && handleReuseCheckpoint(cp)}
                              title={cp.name}
                            >
                              <span className="pb-reuse-chip__dot" />
                              {cp.code || cp.name}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Existing patrols -- collapsible */}
              {selectedSiteId && (
                <div className={`pb-disclosure${existingOpen ? ' pb-disclosure--open' : ''}`}>
                  <button type="button" className="pb-disclosure__head" onClick={() => setExistingOpen((o) => !o)}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><circle cx="6" cy="6" r="2" /><circle cx="18" cy="18" r="2" /><path d="M6 8v4a4 4 0 004 4h4a4 4 0 014 4" /></svg>
                    Existing patrols
                    <small>{existingPatrols.length}</small>
                    <span className="pb-disclosure__chev">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6" /></svg>
                    </span>
                  </button>
                  {existingOpen && (
                    <div className="pb-disclosure__body">
                      {existingPatrols.length === 0 ? (
                        <div style={{ fontSize: 11, color: 'var(--g-300)', padding: '4px 0' }}>
                          {t('No patrols for this site yet.')}
                        </div>
                      ) : (
                        <div className="pb-existing">
                          {existingPatrols.map((patrol) => (
                            <div key={patrol.id} className="pb-existing__item">
                              <div className="pb-existing__main">
                                <span className="pb-existing__name">{patrol.name}</span>
                                <span className="pb-existing__meta">
                                  {patrol.estimated_duration_minutes ? `${patrol.estimated_duration_minutes} min` : ''}
                                  {patrol.is_loop ? ' · loop' : ''}
                                </span>
                                {renderPatrolRouteStatus(patrol)}
                              </div>
                              <div className="pb-existing__actions">
                                <button type="button" className="pb-existing__view" onClick={() => handleShowPatrol(patrol)}>
                                  {showPatrolId === patrol.id ? 'Hide' : 'Show'}
                                </button>
                                <button type="button" className="pb-existing__edit" onClick={() => editingPatrol?.id === patrol.id ? handleCancelEdit() : handleEditPatrol(patrol)}>
                                  {editingPatrol?.id === patrol.id ? 'Cancel' : 'Edit'}
                                </button>
                                <button type="button" className="pb-existing__qr" onClick={() => handleOpenQrModal(patrol)}>QR</button>
                                <button type="button" className="pb-existing__del" onClick={() => setDeleteConfirmPatrolId(patrol.id)}>{t('Del')}</button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

            </div>
          </aside>

          {/* â”€â”€ MAP CANVAS â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
          <main className="pb-canvas" data-tour="patrol-builder-map">
            <div className="pb-map">
              <MapLibreMap
                ref={mapRef}
                initialViewState={DEFAULT_VIEW_STATE}
                style={{ width: '100%', height: '100%' }}
                cursor={draggingRouteStopId ? 'grabbing' : 'crosshair'}
                onClick={handleMapClick}
              >
                {siteGeofenceFeature ? (
                  <Source id="selected-site-geofence-source" type="geojson" data={siteGeofenceFeature}>
                    <Layer id="selected-site-geofence-fill" type="fill" paint={{ 'fill-color': '#22c7a8', 'fill-opacity': 0.12 }} />
                    <Layer id="selected-site-geofence-line" type="line" paint={{ 'line-color': '#22c7a8', 'line-width': 2.5, 'line-opacity': 0.88 }} />
                  </Source>
                ) : null}

                {routeFeature ? (
                  <Source id="patrol-route-source" type="geojson" data={routeFeature}>
                    <Layer id="patrol-route-layer" type="line" paint={{ 'line-color': '#5b7cff', 'line-width': 4, 'line-opacity': 0.95 }} />
                  </Source>
                ) : null}

                {previewRouteFeature ? (
                  <Source id="patrol-preview-route-source" type="geojson" data={previewRouteFeature}>
                    <Layer id="patrol-preview-route-layer" type="line" paint={{ 'line-color': '#ff8f4d', 'line-width': 4, 'line-opacity': 0.92, 'line-dasharray': [3, 2] }} />
                  </Source>
                ) : null}

                {previewRecordedFeature ? (
                  <Source id="patrol-preview-recorded-source" type="geojson" data={previewRecordedFeature}>
                    <Layer id="patrol-preview-recorded-layer" type="line" paint={{ 'line-color': '#ff8f4d', 'line-width': 4, 'line-opacity': 0.95 }} />
                  </Source>
                ) : null}

                {availableCheckpoints.map((cp) => (
                  cp.lat != null && cp.lng != null ? (
                    <MapDotMarker
                      key={`available-${cp.id}`}
                      position={[cp.lat, cp.lng]}
                      color="#ff8f4d"
                      opacity={0.9}
                      size={16}
                      title={`Reuse ${cp.name}`}
                      onClick={() => handleReuseCheckpoint(cp)}
                    />
                  ) : null
                ))}

                {routeStops.map((stop, index) => {
                  const isFirst = index === 0;
                  const canLoop = isFirst && routeStops.length >= 2;
                  const title = canLoop
                    ? `${stop.label} — click to ${completeLoop ? 'remove loop' : 'complete loop'}`
                    : `${stop.label} - ${stop.source === 'existing' ? `Reuse ${stop.sourceName}` : 'New checkpoint'}`;
                  return (
                    <MapPinMarker
                      key={stop.id}
                      position={[stop.lat, stop.lng]}
                      color={selectedRouteStopId === stop.id ? '#22c7a8' : (completeLoop && isFirst) ? '#22c7a8' : stop.qrRequired ? '#5b7cff' : '#879bb9'}
                      label={index + 1}
                      size="lg"
                      title={title}
                      draggable
                      onClick={() => {
                        markerClickRef.current = true;
                        window.setTimeout(() => { markerClickRef.current = false; }, 0);
                        if (justDraggedRef.current) return;
                        if (canLoop) {
                          setCompleteLoop((c) => !c);
                        } else {
                          setSelectedRouteStopId(stop.id);
                        }
                      }}
                      onContextMenu={() => handleRemoveRouteStop(stop.id)}
                      onDragStart={() => handleRouteStopDragStart(stop.id)}
                      onDrag={(event) => handleRouteStopDrag(stop.id, event)}
                      onDragEnd={(event) => handleRouteStopDragEnd(stop.id, event)}
                    />
                  );
                })}

                {showPatrolCheckpoints.map((cp) => (
                  <MapPinMarker
                    key={`preview-${cp.order}-${cp.label}`}
                    position={[cp.lat, cp.lng]}
                    color={cp.qrRequired ? '#ff8f4d' : '#c89a7b'}
                    label={cp.order}
                    title={`${cp.label}${cp.qrRequired ? '' : ' (route only)'}`}
                  />
                ))}
              </MapLibreMap>
            </div>

            {/* Blocker when no site selected */}
            {!selectedSiteId && (
              <div className="pb-map__blocker">
                <div className="pb-map__blocker-icon">
                  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2a7 7 0 017 7c0 5-7 13-7 13S5 14 5 9a7 7 0 017-7z" /><circle cx="12" cy="9" r="2.5" /></svg>
                </div>
                <strong>{t('Pick a client and site to start')}</strong>
                <span>{t('The selected site\'s geofence and saved checkpoints appear here. Click anywhere inside to start dropping route stops.')}</span>
              </div>
            )}

            {/* Stats strip -- top center */}
            {selectedSiteId && (
              <div className="pb-overlay pb-overlay--top">
                <div className="pb-glass pb-stats">
                  <div className="pb-stats__cell">{t('Stops')} <strong>{routeStops.length}</strong></div>
                  <div className={`pb-stats__cell${routeStops.length >= 2 ? ' pb-stats__cell--ok' : ''}`}>
                    Distance <strong>{formatDistance(routeDistanceMetres)}</strong>
                  </div>
                  <div className="pb-stats__cell">
                    Est. time <strong>{estDurationMinutes ? `${estDurationMinutes} min` : '—'}</strong>
                  </div>
                  <div className={`pb-stats__cell${completeLoop ? ' pb-stats__cell--ok' : ''}`}>
                    Loop <strong>{completeLoop ? 'on' : 'off'}</strong>
                  </div>
                  <div className="pb-stats__cell">
                    QR <strong>{routeStops.filter((s) => s.qrRequired).length}</strong>
                  </div>
                </div>
              </div>
            )}

            {/* Mode pill -- top left */}
            {selectedSiteId && (
              <div className="pb-overlay pb-overlay--tl">
                <span className="pb-mode-pill">
                  <span className="pb-mode-pill__dot" />
                  {drawMode === 'draw' ? 'Draw mode · click to add' : 'Reuse mode · tap orange dots'}
                </span>
              </div>
            )}

            {/* Zoom controls -- top right */}
            {selectedSiteId && (
              <div className="pb-overlay pb-overlay--tr">
                <div className="pb-zoom">
                  <button type="button" className="pb-zoom__btn" title={t('Zoom in')} onClick={() => mapRef.current?.zoomIn()}>+</button>
                  <button type="button" className="pb-zoom__btn" title={t('Zoom out')} onClick={() => mapRef.current?.zoomOut()}>-</button>
                </div>
              </div>
            )}

            {/* Legend -- bottom left */}
            {selectedSiteId && (
              <div className="pb-overlay pb-overlay--bl">
                <div className="pb-glass pb-legend">
                  <div className="pb-legend__title">{t('Legend')}</div>
                  <div className="pb-legend__row"><span className="pb-legend__swatch pb-legend__swatch--geo" />{t('Site geofence')}</div>
                  <div className="pb-legend__row"><span className="pb-legend__swatch pb-legend__swatch--route" />{t('Patrol route')}</div>
                  <div className="pb-legend__row"><span className="pb-legend__swatch pb-legend__swatch--reuse" />{t('Site checkpoint')}</div>
                  {showPatrolId && <div className="pb-legend__row"><span className="pb-legend__swatch pb-legend__swatch--preview" />{t('Saved patrol')}</div>}
                  {previewRecordedFeature && <div className="pb-legend__row"><span className="pb-legend__swatch pb-legend__swatch--recorded" />{t('Recorded route')}</div>}
                </div>
              </div>
            )}

            {/* Action bar -- bottom center */}
            {selectedSiteId && (
              <div className="pb-overlay pb-overlay--bc">
                <div className="pb-glass pb-actionbar">
                  <button type="button" className="pb-actionbar__btn" onClick={handleUndoLast} disabled={routeStops.length === 0}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><path d="M3 7v6h6" /><path d="M3 13a9 9 0 1 0 3-7l-3 3" /></svg>
                    {t('Undo')}
                  </button>
                  {clearConfirmPending ? (
                    <div className="pb-actionbar__confirm">
                      <span className="pb-actionbar__confirm-label">{t('Clear all stops?')}</span>
                      <button
                        type="button"
                        className="pb-actionbar__confirm-yes"
                        onClick={() => { handleClearRoute(); setClearConfirmPending(false); }}
                      >
                        {t('Yes, clear')}
                      </button>
                      <button
                        type="button"
                        className="pb-actionbar__confirm-no"
                        onClick={() => setClearConfirmPending(false)}
                      >
                        {t('Cancel')}
                      </button>
                    </div>
                  ) : (
                    <button type="button" className="pb-actionbar__btn" onClick={() => setClearConfirmPending(true)} disabled={routeStops.length === 0}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><path d="M20 20H7l-4-4 9-9 7 7-5 5" /><line x1="14" y1="6" x2="20" y2="12" /></svg>
                      {t('Clear')}
                    </button>
                  )}
                  <div className="pb-actionbar__div" />
                  <button
                    type="button"
                    className={`pb-actionbar__btn${completeLoop ? ' pb-actionbar__btn--accent' : ''}`}
                    onClick={() => routeStops.length >= 2 && setCompleteLoop((c) => !c)}
                    disabled={routeStops.length < 2}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><path d="M17 1l4 4-4 4" /><path d="M3 11V9a4 4 0 014-4h14" /><path d="M7 23l-4-4 4-4" /><path d="M21 13v2a4 4 0 01-4 4H3" /></svg>
                    {completeLoop ? 'Loop on' : 'Close loop'}
                  </button>
                  <div className="pb-actionbar__div" />
                  <button
                    type="button"
                    className="pb-actionbar__btn pb-actionbar__btn--primary"
                    onClick={handleSubmit}
                    disabled={isSubmitting || !name.trim() || routeStops.length === 0}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><path d="M5 13l4 4L19 7" /></svg>
                    {isSubmitting ? t('Saving...') : routeStops.length > 0 ? t('Save patrol') : t('Add stop')}
                  </button>
                </div>
              </div>
            )}
          </main>

          {/* â”€â”€ RIGHT INSPECTOR â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
          {selectedRouteStop ? (
            <aside className="pb-inspector" data-tour="patrol-builder-checkpoint">
              <div className="pb-inspector__head">
                <div className="pb-inspector__title-row">
                  <div className="pb-inspector__pin">{selectedRouteStopIndex + 1}</div>
                  <input
                    className="pb-inspector__title"
                    value={selectedRouteStop.label}
                    onChange={(e) => handleLabelChange(selectedRouteStop.id, e.target.value)}
                    placeholder={getDefaultCheckpointLabel(selectedRouteStopIndex)}
                  />
                  <button type="button" className="pb-inspector__close" onClick={() => setSelectedRouteStopId(null)} title={t('Close')}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6L6 18M6 6l12 12" /></svg>
                  </button>
                </div>
                <div className="pb-inspector__sub">
                  <span className={`pb-tag${selectedRouteStop.source === 'existing' ? ' pb-tag--reuse' : ' pb-tag--route'}`}>
                    {selectedRouteStop.source === 'existing' ? 'Reused' : 'New'}
                  </span>
                  {selectedRouteStop.qrRequired ? (
                    <span className="pb-tag pb-tag--accent">{t('QR required')}</span>
                  ) : (
                    <span className="pb-tag">{t('Route only')}</span>
                  )}
                  <span className="pb-tag pb-tag--mono">
                    {selectedRouteStopIndex + 1} of {routeStops.length}
                  </span>
                </div>
              </div>

              <div className="pb-inspector__body">
                {error && <div className="pb-error-banner">{error}</div>}

                <div className="pb-field">
                  <label className="pb-field__label">{t('Checkpoint name')}</label>
                  <input
                    className="pb-input"
                    value={getSavedCheckpointName(selectedRouteStop)}
                    onChange={(e) => handleCheckpointNameChange(selectedRouteStop.id, e.target.value)}
                  />
                </div>

                <div className="pb-site__row">
                  <div className="pb-field">
                    <label className="pb-field__label">{t('Code')}</label>
                    <input
                      className="pb-input pb-input--mono"
                      value={selectedRouteStop.code ?? ''}
                      placeholder="optional"
                      onChange={(e) => handleCheckpointCodeChange(selectedRouteStop.id, e.target.value)}
                    />
                  </div>
                  <div className="pb-field">
                    <label className="pb-field__label">{t('Coordinates')}</label>
                    <div className="pb-input pb-input--mono" style={{ display: 'flex', alignItems: 'center', fontSize: 10 }}>
                      {formatCoordinates(selectedRouteStop.lat, selectedRouteStop.lng)}
                    </div>
                  </div>
                </div>

                <div className="pb-field">
                  <label className="pb-field__label">
                    Geofence radius &mdash; {selectedRouteStop.geofenceRadius} m
                  </label>
                  <div className="pb-radius-slider">
                    <div className="pb-radius-slider__row">
                      <input
                        type="range"
                        min="10"
                        max="500"
                        step="5"
                        value={selectedRouteStop.geofenceRadius}
                        style={{ '--p': `${((selectedRouteStop.geofenceRadius - 10) / 490) * 100}%` } as React.CSSProperties}
                        onChange={(e) => handleGeofenceRadiusChange(selectedRouteStop.id, e.target.value)}
                      />
                      <span className="pb-radius-slider__value">{selectedRouteStop.geofenceRadius} m</span>
                    </div>
                  </div>
                </div>

                <div className="pb-toggle-row">
                  <div className="pb-toggle-row__copy">
                    <strong>{t('Require QR scan')}</strong>
                    <small>{t('Guard must scan the checkpoint QR for the patrol to advance.')}</small>
                  </div>
                  <button
                    type="button"
                    className={`pb-switch${selectedRouteStop.qrRequired ? ' pb-switch--on' : ''}`}
                    onClick={() => handleToggleQrRequired(selectedRouteStop.id, !selectedRouteStop.qrRequired)}
                  />
                </div>

                <button
                  type="button"
                  className="pb-btn pb-btn--danger"
                  style={{ width: '100%', justifyContent: 'center' }}
                  onClick={() => handleRemoveRouteStop(selectedRouteStop.id)}
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" /></svg>
                  {t('Remove stop')}
                </button>
              </div>
            </aside>
          ) : (
            <aside className="pb-inspector" data-tour="patrol-builder-checkpoint">
              <div className="pb-inspector__body">
                <div className="pb-empty-pinned">
                  <div className="pb-empty-pinned__icon">
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><line x1="12" y1="3" x2="12" y2="6" /><line x1="12" y1="18" x2="12" y2="21" /><line x1="3" y1="12" x2="6" y2="12" /><line x1="18" y1="12" x2="21" y2="12" /></svg>
                  </div>
                  <h3>{t('Click the map to start')}</h3>
                  <p>{t('Drop checkpoints inside the site geofence. Select a stop to configure its name, geofence radius, and QR scan rule.')}</p>
                </div>
                {error && <div className="pb-error-banner">{error}</div>}
                <div className="pb-shortcuts">
                  <div className="pb-shortcuts__title">{t('Shortcuts')}</div>
                  <div className="pb-shortcuts__row"><span>{t('Add stop')}</span><span className="pb-shortcuts__kbd">{t('Click map')}</span></div>
                  <div className="pb-shortcuts__row"><span>{t('Reposition')}</span><span className="pb-shortcuts__kbd">{t('Drag pin')}</span></div>
                  <div className="pb-shortcuts__row"><span>{t('Remove stop')}</span><span className="pb-shortcuts__kbd">{t('Right-click')}</span></div>
                  <div className="pb-shortcuts__row"><span>{t('Undo last')}</span><span className="pb-shortcuts__kbd">{t('Undo btn')}</span></div>
                  <div className="pb-shortcuts__row"><span>{t('Toggle loop')}</span><span className="pb-shortcuts__kbd">{t('Loop btn')}</span></div>
                </div>
              </div>
            </aside>
          )}

        </div>{/* /pb-body */}
      </div>{/* /pb-app */}

      {/* QR Modal */}
      {qrModalPatrol ? (
        <div className="pb-qr-overlay">
          <div className="pb-qr-modal">
            <div className="pb-qr-modal__head">
              <div>
                <div className="pb-qr-modal__title">QR Codes &mdash; {qrModalPatrol.name}</div>
                <div className="pb-qr-modal__sub">{t('Only QR-required checkpoints are included.')}</div>
              </div>
              <button type="button" className="pb-qr-modal__close" onClick={() => setQrModalPatrol(null)}>x</button>
            </div>
            <div className="pb-qr-modal__body">
              {qrLoading ? (
                <div className="loading"><div className="spinner" /></div>
              ) : qrData.length === 0 ? (
                <div className="pb-qr-modal__empty">{t('No QR-required checkpoints for this patrol.')}</div>
              ) : (
                <div className="pb-qr-grid">
                  {qrData.map((cp) => (
                    <div key={`${cp.name}-${cp.tokenId ?? 'none'}`} className="pb-qr-item">
                      {cp.dataUrl ? <img src={cp.dataUrl} alt={cp.name} /> : <div className="pb-qr-placeholder">{t('No QR')}</div>}
                      <p className="pb-qr-item__label">{cp.name}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {!qrLoading && qrData.length > 0 && (
              <div className="pb-qr-modal__foot">
                <button type="button" className="pb-btn pb-btn--ghost" onClick={handleDownloadQrPdf}>{t('Download PDF')}</button>
                <button type="button" className="pb-btn pb-btn--primary" onClick={handlePrintQr}>{t('Print')}</button>
              </div>
            )}
          </div>
        </div>
      ) : null}

      {deleteConfirmPatrolId && (
        <ConfirmDeleteModal
          title={t('Delete patrol')}
          isDeleting={isDeleting}
          onConfirm={handleConfirmDelete}
          onCancel={() => setDeleteConfirmPatrolId(null)}
        />
      )}

      {exitConfirmPending && onExit && (
        <div className="pb-exit-overlay">
          <div className="pb-exit-modal">
            <div className="pb-exit-modal__icon">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
            </div>
            <h2 className="pb-exit-modal__title">{t('Leave patrol builder?')}</h2>
            <p className="pb-exit-modal__body">
              {t('You have an unsaved patrol in progress. Leaving now will discard all stops and settings.')}
            </p>
            <div className="pb-exit-modal__actions">
              <button type="button" className="pb-btn pb-btn--ghost" onClick={() => setExitConfirmPending(false)}>
                {t('Stay and finish')}
              </button>
              <button type="button" className="pb-btn pb-btn--danger" onClick={onExit}>
                {t('Leave anyway')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

